import React, { useState, useEffect } from 'react';
import { Text, View, FlatList, Pressable, useWindowDimensions } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { savePerson } from "@/data/people";
import Avatar from '@/components/People/Avatar';
import { deleteRegistration, registerUserWithOrganization, withdrawInvite, sendInvitation } from '@/data/registrations';
import { getOrganization, getCurrentOrgID } from '@/data/organization';
import useLoader from '@/utils/hooks/useLoader';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ContentLoader from '@/components/General/ContentLoader';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { updateNotifications } from '@/data/notifications';
import SolidButton from '@/components/General/SolidButton';
import { showAlert } from '@/utils/showAlert';
import { usePathname, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import usePlatform from '@/utils/hooks/usePlatform';

export default function RegistrationsScreen() {
    /*** State ***/
    const [processingID, setProcessingID] = useState(null);
    const [processingDeny, setProcessingDeny] = useState(null);
    const [customLinkPerson, setCustomLinkPerson] = useState({});
    const [expandedCards, setExpandedCards] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const [tabRoutes] = useState([
        { key: 'registrations', title: 'Registrations' },
        { key: 'invites', title: 'Invites' }
    ]);
    const [withdrawingID, setWithdrawingID] = useState(null);

    /*** Theme & Dimensions ***/
    const router = useRouter();
    const pathname = usePathname();
    const layout = useWindowDimensions();
    const { isNative } = usePlatform();
    const loader = useLoader();

    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()

    if(!loggedInUser?.isAdmin){
        router.back();
    }

    const { data: registrations, isFetching: loadingRegistrations } = Query.registrations.all();
    const { data: invites, isFetching: loadingInvites } = Query.invites.all();

    const markAsRead = useMutation({ mutationFn: updateNotifications})
    const deleteRegistrationMutation = useMutation({
        mutationFn: deleteRegistration,
        onSuccess: () => {
            setProcessingID(null);
            setProcessingDeny(null)

            queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})
        }
    });


    const registerUserMutation = useMutation({
        mutationFn: registerUserWithOrganization,
        onSuccess: async (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.people.byId(data.person.id)});
            Toast.show({type:"success", text2:"New user registered successfully! They will now be able to log into your organization."});
        }
    })

    const createPersonMutation = useMutation({
        mutationFn: savePerson,
        onSuccess: async (data, variables) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.people.all()});

            registerUserMutation.mutate({person: data, userID: variables?.userID})
            deleteRegistrationMutation.mutate({id: variables?.registrationID, denied: false});

            setProcessingID(null);
            setProcessingDeny(null)
            
        }
    })

    const withdrawInviteMutation = useMutation({
        mutationFn: withdrawInvite,
        onSuccess: () => {
            setWithdrawingID(null);
            queryClient.invalidateQueries({queryKey: QueryKeys.invites.all()})
        }
    })

    const handleWithdrawInvite = (invite) => {
        const name = invite.Person ? `${invite.Person.firstName} ${invite.Person.lastName}` : invite.email;
        showAlert({
            title: 'Withdraw invitation?',
            subtitle: `The invitation to ${name} will be revoked and they will no longer be able to use it to join.`,
            onConfirm: () => {
                setWithdrawingID(invite.id);
                withdrawInviteMutation.mutate({ id: invite.id });
            },
        });
    }

    const handleRemoveInvite = (invite) => {
        setWithdrawingID(invite.id);
        withdrawInviteMutation.mutate({ id: invite.id });
    }

    const handleResendInvite = async (invite) => {
        const name = invite.Person ? `${invite.Person.firstName} ${invite.Person.lastName}` : invite.email;
        showAlert({
            title: `Resend invitation to ${name}?`,
            subtitle: `A new invitation email will be sent to ${invite.email}`,
            confirmText: "Resend Invite",
            onConfirm: async () => {
                try {
                    loader.enable();
                    const orgID = await getCurrentOrgID();
                    const org = await getOrganization(orgID);
                    await withdrawInviteMutation.mutateAsync({ id: invite.id });
                    await sendInvitation({
                        personID: invite.Person?.id,
                        email: invite.email,
                        firstName: invite.Person?.firstName || invite.email,
                        organizationName: org?.name,
                        inviterName: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`
                    });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.invites.all() });
                    loader.disable();
                } catch {
                    loader.disable();
                }
            }
        });
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    const isExpired = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    }

    /*** Effects ***/
    useEffect(() => {
        const curentUnreadNotifications = loggedInUser?.Notifications?.filter(n => n.notificationType == "NEW_USER_REQUEST" && !n.isRead)

        if(curentUnreadNotifications?.length > 0){
            markAsRead.mutateAsync(curentUnreadNotifications.map(n => ({id: n.id, isRead: true}))).then(() => {
                queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()})
            })
        }
    }, [])

    const toggleExpanded = (id) => {
        setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
    }

    const handleLinkPerson = (item, person) => {
        showAlert({
            title: `Link to "${person.firstName} ${person.lastName}"?`,
            subtitle: 'This profile will be updated with the registration info and linked to their account.',
            onConfirm: () => {
                setProcessingID(item.id);
                setProcessingDeny(false);

                registerUserMutation.mutate({userID: item.User?.id, person: {
                    id: person.id,
                    firstName: item.User?.firstName,
                    lastName: item.User?.lastName,
                    email: item.User?.email
                }})

                deleteRegistrationMutation.mutate({id: item.id, denied: false});
                queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})

                setProcessingID(null);
                setProcessingDeny(null)
            },
        });
    }

    const handleCreateProfile = (item) => {
        if(!loggedInUser?.isAdmin) return false;

        setProcessingID(item.id);
        setProcessingDeny(false);

        createPersonMutation.mutate({
            userID: item.User?.id,
            registrationID: item.id,
            firstName: item.User?.firstName,
            lastName: item.User?.lastName,
            email: item.User?.email,
            organizationID: loggedInUser?.organizationID
        });
    }

    const handleDenyRequest = (item) => {
        showAlert({
            title: `Deny this registration request?`,
            subtitle: `${item.User?.firstName} ${item.User?.lastName} will not be able to access your organization.`,
            onConfirm: () => {
                setProcessingID(item.id);
                setProcessingDeny(true)

                deleteRegistrationMutation.mutate({id: item.id, denied: true})
                queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})
            },
        });
    }

    const handleCustomLink = (item) => {
        const person = customLinkPerson[item.id];
        if(!person?.id) return;

        handleLinkPerson(item, person);
    }

    const renderMatchedPerson = (person, item) => {
        const nameMatches = person.firstName === item.User?.firstName && person.lastName === item.User?.lastName;
        const emailMatches = person.email === item.User?.email;

        return (
            <View key={person.id} style={styles.matchCard}>
                <View style={styles.matchInfo}>
                    <Avatar profile={person} disableBorder size={40}/>
                    <View style={styles.matchDetails}>
                        <View style={styles.matchNameRow}>
                            <Text style={styles.matchName}>{person.firstName} {person.lastName}</Text>
                            {nameMatches && <Unicon name="checkmark-circle" size={14} colorVariant="success" />}
                        </View>
                        <View style={styles.matchEmailRow}>
                            <Text style={styles.matchEmail(emailMatches)}>{person.email || 'No email'}</Text>
                            {emailMatches && <Unicon name="checkmark-circle" size={12} colorVariant="success" />}
                        </View>
                    </View>
                </View>
                <SolidButton 
                    onPress={() => handleLinkPerson(item, person)} 
                    type="primary" 
                    size="sm" 
                    text="Link"
                    iconName="link-outline"
                    iconBefore
                />
            </View>
        );
    }

    const renderItem = ({item}) => {
        const isExpanded = expandedCards[item.id];
        const isProcessing = processingID === item.id;
        
        const sortedMatches = item.matchedPeople?.sort((a, b) => {
            let matchesA = 0;
            let matchesB = 0;

            if(a.firstName === item.User?.firstName && a.lastName === item.User?.lastName) matchesA++;
            if(a.email === item.User?.email) matchesA++;
            if(b.firstName === item.User?.firstName && b.lastName === item.User?.lastName) matchesB++;
            if(b.email === item.User?.email) matchesB++;

            return matchesB - matchesA;
        }) || [];

        const hasMatches = sortedMatches.length > 0;

        return (
            <View style={styles.card}>
                {/* Header Section */}
                <View style={styles.cardHeader}>
                    <View style={styles.userInfo}>
                        <View style={styles.avatarContainer}>
                            <View style={styles.avatarPlaceholder}>
                                <Text style={styles.avatarInitials}>
                                    {item.User?.firstName?.[0]}{item.User?.lastName?.[0]}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.userDetails}>
                            <Text style={styles.userName}>{item.User?.firstName} {item.User?.lastName}</Text>
                            <Text style={styles.userEmail}>{item.User?.email}</Text>
                        </View>
                    </View>
                    <View style={styles.statusBadge}>
                        <Unicon name="time-outline" size={12} colorVariant="warning" />
                        <Text style={styles.statusText}>Pending</Text>
                    </View>
                </View>

                {/* Quick Actions */}
                <View style={styles.quickActions}>
                    <SolidButton
                        disabled={processingDeny === true && isProcessing} 
                        processing={processingDeny === false && isProcessing} 
                        type="success" 
                        size='sm'
                        text="Accept & Create Profile"
                        iconName="person-add-outline"
                        iconBefore
                        onPress={() => handleCreateProfile(item)}
                    />
                    <SolidButton 
                        disabled={processingDeny === false && isProcessing} 
                        processing={processingDeny === true && isProcessing} 
                        type="error"
                        size='sm'
                        variant="outlined"
                        text="Deny"
                        iconName="close-outline"
                        iconBefore
                        onPress={() => handleDenyRequest(item)}
                    />
                </View>

                {/* Expandable Section Toggle */}
                <Pressable style={styles.expandToggle} onPress={() => toggleExpanded(item.id)}>
                    <View style={styles.expandToggleContent}>
                        <Unicon name="people-outline" size={16} colorVariant="textSecondary" />
                        <Text style={styles.expandToggleText}>
                            {hasMatches 
                                ? `${sortedMatches.length} potential match${sortedMatches.length > 1 ? 'es' : ''} found`
                                : 'Link to existing profile'
                            }
                        </Text>
                    </View>
                    <Unicon 
                        name={isExpanded ? "chevron-up" : "chevron-down"} 
                        size={18} 
                        colorVariant="textSecondary" 
                    />
                </Pressable>

                {/* Expanded Content */}
                {isExpanded && (
                    <View style={styles.expandedContent}>
                        {/* Matched People Section */}
                        {hasMatches ? (
                            <View style={styles.matchesSection}>
                                <Text style={styles.sectionLabel}>Potential Matches</Text>
                                <Text style={styles.sectionHint}>
                                    These profiles match the registration info. Link to update and connect.
                                </Text>
                                {sortedMatches.map(person => renderMatchedPerson(person, item))}
                            </View>
                        ) : (
                            <View style={styles.noMatchesSection}>
                                <Unicon name="alert-circle-outline" size={24} colorVariant="warning" />
                                <Text style={styles.noMatchesText}>
                                    No matching profiles found. Verify their identity before creating a new profile.
                                </Text>
                            </View>
                        )}

                        {/* Manual Link Section */}
                        <View style={styles.manualLinkSection}>
                            <Text style={styles.sectionLabel}>Link to Someone Else</Text>
                            <View style={styles.manualLinkRow}>
                                <View style={styles.selectorWrapper}>
                                    <SheetSelector 
                                        type="people" 
                                        title="Search People" 
                                        placeholder="Search for a person..."
                                        fromScreen={pathname} 
                                        value={customLinkPerson[item.id]} 
                                        onChange={(selected) => {
                                            setCustomLinkPerson(prev => ({ ...prev, [item.id]: selected }));
                                        }}
                                    />
                                </View>
                                <View style={styles.linkButtonWrapper}>
                                    <SolidButton
                                        disabled={!customLinkPerson[item.id]?.id} 
                                        type="primary" 
                                        size='sm'
                                        text="Link"
                                        iconName="link-outline"
                                        iconBefore
                                        onPress={() => handleCustomLink(item)}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                )}
            </View>
        );
    }

    const renderInviteItem = ({item}) => {
        const expired = isExpired(item.expiresAt);
        const isWithdrawing = withdrawingID === item.id;
        const personName = item.Person ? `${item.Person.firstName} ${item.Person.lastName}` : null;
        const inviterName = item.InvitedBy ? `${item.InvitedBy.firstName} ${item.InvitedBy.lastName}` : null;

        return (
            <View style={styles.card}>
                <View style={styles.cardHeader}>
                    <View style={styles.userInfo}>
                        {item.Person ? (
                            <Avatar profile={item.Person} disableBorder size={48} />
                        ) : (
                            <View style={styles.avatarContainer}>
                                <View style={styles.inviteAvatarPlaceholder}>
                                    <Unicon name="mail-outline" size={22} colorVariant="onPrimary" />
                                </View>
                            </View>
                        )}
                        <View style={styles.userDetails}>
                            {personName && <Text style={styles.userName}>{personName}</Text>}
                            <Text style={personName ? styles.userEmail : styles.userName}>{item.email}</Text>
                        </View>
                    </View>
                    <View style={expired ? styles.expiredBadge : styles.inviteBadge}>
                        <Unicon name={expired ? "alert-circle-outline" : "mail-outline"} size={12} colorVariant={expired ? "error" : "info"} />
                        <Text style={expired ? styles.expiredBadgeText : styles.inviteBadgeText}>
                            {expired ? 'Expired' : 'Sent'}
                        </Text>
                    </View>
                </View>

                <View style={styles.inviteDetails}>
                    {inviterName && (
                        <View style={styles.inviteDetailRow}>
                            <Text style={styles.inviteDetailLabel}>Invited by</Text>
                            <Text style={styles.inviteDetailValue}>{inviterName}</Text>
                        </View>
                    )}
                    {item.createdAt && (
                        <View style={styles.inviteDetailRow}>
                            <Text style={styles.inviteDetailLabel}>Sent</Text>
                            <Text style={styles.inviteDetailValue}>{formatDate(item.createdAt)}</Text>
                        </View>
                    )}
                    <View style={styles.inviteDetailRow}>
                        <Text style={styles.inviteDetailLabel}>Expires</Text>
                        <Text style={expired ? styles.inviteDetailValueExpired : styles.inviteDetailValue}>
                            {formatDate(item.expiresAt)}
                        </Text>
                    </View>
                </View>

                <View style={styles.inviteActions}>
                    {expired ? (
                        <>
                            <SolidButton
                                processing={isWithdrawing}
                                type="error"
                                size="sm"
                                variant="outlined"
                                text="Remove"
                                iconName="trash-outline"
                                iconBefore
                                onPress={() => handleRemoveInvite(item)}
                            />
                            <SolidButton
                                type="primary"
                                size="sm"
                                text="Resend Invite"
                                iconName="mail-outline"
                                iconBefore
                                onPress={() => handleResendInvite(item)}
                            />
                        </>
                    ) : (
                        <SolidButton
                            processing={isWithdrawing}
                            type="error"
                            size="sm"
                            variant="outlined"
                            text="Withdraw Invite"
                            iconName="close-circle-outline"
                            iconBefore
                            onPress={() => handleWithdrawInvite(item)}
                        />
                    )}
                </View>
            </View>
        );
    }

    const renderRegistrationsTab = () => (
        <FlatList
            data={loadingRegistrations ? [] : registrations}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            removeClippedSubviews
            renderItem={renderItem}
            progressViewOffset={1000}
            refreshing={false}
            onRefresh={() => queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})}
            ListHeaderComponent={
                !loadingRegistrations && registrations?.length > 0 ? (
                    <View style={styles.listHeader}>
                        <Text style={styles.listHeaderTitle}>Registration Requests</Text>
                        <Text style={styles.listHeaderSubtitle}>
                            {registrations.length} pending request{registrations.length > 1 ? 's' : ''}
                        </Text>
                    </View>
                ) : null
            }
            ListFooterComponent={
                !loadingRegistrations 
                    ? registrations?.length === 0 
                        ? <EmptyListPlaceholder icon="document-outline" spacing={10} message="No pending registration requests"/> 
                        : <View style={styles.listFooter}/>
                    : (
                        <View style={styles.loaderContainer}>
                            <ContentLoader containerHeight={180}/>
                            <ContentLoader containerHeight={180} staggerPosition={1}/>
                            <ContentLoader containerHeight={180} staggerPosition={2}/>
                        </View>
                    )
            }
        />
    );

    const renderInvitesTab = () => (
        <FlatList
            data={loadingInvites ? [] : invites}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            removeClippedSubviews
            renderItem={renderInviteItem}
            progressViewOffset={1000}
            refreshing={false}
            onRefresh={() => queryClient.invalidateQueries({queryKey: QueryKeys.invites.all()})}
            ListHeaderComponent={
                !loadingInvites && invites?.length > 0 ? (
                    <View style={styles.listHeader}>
                        <Text style={styles.listHeaderTitle}>Sent Invitations</Text>
                        <Text style={styles.listHeaderSubtitle}>
                            {invites.length} invitation{invites.length > 1 ? 's' : ''}
                        </Text>
                    </View>
                ) : null
            }
            ListFooterComponent={
                !loadingInvites 
                    ? invites?.length === 0 
                        ? <EmptyListPlaceholder icon="mail-outline" spacing={10} message="No pending invitations"/> 
                        : <View style={styles.listFooter}/>
                    : (
                        <View style={styles.loaderContainer}>
                            <ContentLoader containerHeight={140}/>
                            <ContentLoader containerHeight={140} staggerPosition={1}/>
                            <ContentLoader containerHeight={140} staggerPosition={2}/>
                        </View>
                    )
            }
        />
    );

    return (
        <View style={styles.container}>
            <Pressable 
                onPress={() => router.push('/roles')} 
                style={styles.manageUsersLink}
            >
                <View style={styles.manageUsersLinkContent}>
                    <Unicon name="people-outline" size={18} colorVariant="primary" />
                    <Text style={styles.manageUsersLinkText}>Manage Registered Users</Text>
                </View>
                <Unicon name="chevron-forward-outline" size={18} colorVariant="primary" />
            </Pressable>
            <TabView
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                onIndexChange={setTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <ReachTabBar {...props} />}
                lazy
                swipeEnabled={isNative}
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'registrations':
                            return renderRegistrationsTab();
                        case 'invites':
                            return renderInvitesTab();
                        default:
                            return null;
                    }
                }}
            />
        </View> 
    );
}


const styles = StyleSheet.create(({colors, fonts, shadows}, rt) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    manageUsersLink: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: colors.primaryContainer,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    manageUsersLinkContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    manageUsersLinkText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    listContent: {
        padding: 16,
        maxWidth: 800,
        alignSelf: 'center',
        width: '100%',
    },
    listHeader: {
        marginBottom: 16,
    },
    listHeaderTitle: {
        fontSize: 24,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    listHeaderSubtitle: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 4,
    },
    listFooter: {
        paddingBottom: 40,
    },
    loaderContainer: {
        padding: 16,
        gap: 16,
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: colors.border,
        overflow: 'hidden',
        boxShadow: shadows.card,
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    userInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    avatarContainer: {
        marginRight: 12,
    },
    avatarPlaceholder: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarInitials: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.onPrimary,
    },
    userDetails: {
        flex: 1,
        paddingLeft:10
    },
    userName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    userEmail: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 2,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.warningContainer,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    statusText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.warning,
        textTransform: 'uppercase',
    },
    quickActions: {
        flexDirection: 'row',
        padding: 16,
        gap: 10,
        flexWrap: 'wrap',
    },
    expandToggle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: colors.surfaceVariant,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    expandToggleContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    expandToggleText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    expandedContent: {
        padding: 16,
        backgroundColor: colors.background,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    matchesSection: {
        marginBottom: 20,
    },
    sectionLabel: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 4,
    },
    sectionHint: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginBottom: 12,
    },
    matchCard: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.surface,
        padding: 12,
        borderRadius: 8,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: colors.border,
        _web: {
            transition: 'border-color 0.15s ease',
            _hover: {
                borderColor: colors.primary,
            },
        },
    },
    matchInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    matchDetails: {
        marginLeft: 12,
        flex: 1,
    },
    matchNameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    matchName: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    matchEmailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 2,
    },
    matchEmail: (matches) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: matches ? colors.success : colors.textSecondary,
    }),
    noMatchesSection: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.warningContainer,
        padding: 12,
        borderRadius: 8,
        gap: 12,
        marginBottom: 20,
    },
    noMatchesText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    manualLinkSection: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: 16,
    },
    manualLinkRow: {
        flexDirection: rt.breakpoint === 'xs' ? 'column' : 'row',
        alignItems: rt.breakpoint === 'xs' ? 'stretch' : 'flex-end',
        gap: 12,
    },
    selectorWrapper: {
        flex: 1,
    },
    linkButtonWrapper: {
        marginBottom: rt.breakpoint === 'xs' ? 0 : 10,
    },
    inviteAvatarPlaceholder: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: colors.info,
        justifyContent: 'center',
        alignItems: 'center',
    },
    inviteBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.infoContainer,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    inviteBadgeText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.info,
        textTransform: 'uppercase',
    },
    expiredBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.errorContainer,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    expiredBadgeText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.error,
        textTransform: 'uppercase',
    },
    inviteDetails: {
        padding: 16,
        gap: 8,
    },
    inviteDetailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    inviteDetailLabel: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    inviteDetailValue: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    inviteDetailValueExpired: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.error,
    },
    inviteActions: {
        flexDirection: 'row',
        padding: 16,
        paddingTop: 0,
        gap: 10,
        flexWrap: 'wrap',
    },
}))